import { createContext, useContext, useLayoutEffect } from 'react';
import { useGT } from 'gt-react';
import type { As400ExportGroup, ChangeSetCommentAnchor } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

/**
 * One cell of a review worksheet to go to: a comment thread's anchor (pass
 * `thread.anchor` as is).
 */
export type ReviewCellTarget = Pick<
  ChangeSetCommentAnchor,
  'group' | 'row' | 'columnKey'
>;

/** Takes a jump to a cell — the review grid, or Review mode's list. */
export type ReviewCellJumpHandler = (target: ReviewCellTarget) => void;

/**
 * Where jumps to a cell land, held outside React state: whatever is showing
 * the review's cells registers itself, and a jump calls whichever did last.
 */
export interface ReviewCellJumpRegistry {
  /** Make `handler` where jumps land; the returned function takes it back. */
  register: (handler: ReviewCellJumpHandler) => () => void;
  /** Hand `target` to the registered handler; false when there is none. */
  jump: (target: ReviewCellTarget) => boolean;
  /**
   * Like `jump`, but with no handler yet, `target` waits for the next one to
   * register — a link into the review can arrive before its grid does.
   */
  jumpWhenReady: (target: ReviewCellTarget) => void;
  /** Hand `target` to the next handler to register, not the current one. */
  holdForNext: (target: ReviewCellTarget) => void;
}

export function createReviewCellJumpRegistry(): ReviewCellJumpRegistry {
  let current: ReviewCellJumpHandler | null = null;
  let waiting: ReviewCellTarget | null = null;
  return {
    register(handler) {
      current = handler;
      if (waiting) {
        const target = waiting;
        waiting = null;
        handler(target);
      }
      return () => {
        if (current === handler) current = null;
      };
    },
    jump(target) {
      if (!current) return false;
      current(target);
      return true;
    },
    jumpWhenReady(target) {
      if (current) current(target);
      else waiting = target;
    },
    holdForNext(target) {
      waiting = target;
    },
  };
}

/** Provided by the review page (see `ReviewCellJumpProvider`). */
export const ReviewCellJumpContext =
  createContext<ReviewCellJumpRegistry | null>(null);

/** The popup for a cell that can't be shown where the review is now. */
export function useCellNotShownNotice(): () => void {
  const gt = useGT();
  const { showToast } = useToast();
  return () =>
    showToast({
      title: gt('Cell not shown', {
        context: "toast title: a comment's cell can't be shown in the grid",
      }),
      message: gt(
        'Its row is not in the grid with the divisions, location, filters and hidden rows and columns as they are now.',
        {
          context:
            "toast body: why going to a comment's cell found nothing to show",
        },
      ),
      color: 'warning',
    });
}

/**
 * Go to a cell of the review (GEN-3261): open its worksheet tab, page to its
 * row under the grid's current filters and sort, select the cell and open its
 * comments. A row the grid can't show as it is filtered now — or a review
 * with no grid on screen — is said in a popup.
 *
 * Call it with a thread's anchor: `jump(thread.anchor)`.
 */
export function useJumpToReviewCell(): ReviewCellJumpHandler {
  const registry = useContext(ReviewCellJumpContext);
  const notShown = useCellNotShownNotice();
  return (target) => {
    if (!registry?.jump(target)) notShown();
  };
}

/**
 * Where the review's cell jumps land while a worksheet shows an empty state
 * instead of its grid (`group`; null while the grid is up): open the target's
 * worksheet and hand the jump to its grid once that registers. A jump to the
 * worksheet already shown can't land there, and says so.
 */
export function useWorksheetSwitchJumpTarget(
  group: As400ExportGroup | null,
  openWorksheet: (group: As400ExportGroup) => void,
): void {
  const registry = useContext(ReviewCellJumpContext);
  const notShown = useCellNotShownNotice();
  useReviewCellJumpTarget(
    group === null
      ? undefined
      : (target) => {
          if (target.group === group) {
            notShown();
            return;
          }
          openWorksheet(target.group);
          registry?.holdForNext(target);
        },
  );
}

/**
 * Makes `handler` where the review's cell jumps land while the caller is
 * mounted (pass undefined to take none). It is re-read on every render, so it
 * may close over the caller's latest props.
 */
export function useReviewCellJumpTarget(
  handler: ReviewCellJumpHandler | undefined,
): void {
  const registry = useContext(ReviewCellJumpContext);
  useLayoutEffect(() => {
    if (!registry || !handler) return;
    return registry.register(handler);
  });
}
